// pages/ExpensePurchaseOrder/EposNotReadyForPayment.js
//
// Legacy menu: "EPOs Not Ready For Payment".
// Legacy form: Projects/epoViewAllOfMy.vb with isMyPending = True.
//
// R41 — replaced the R40 radio facet bar with per-column filters.
// Backend: GET /api/Epo/all?type=PENDINGRECEIPT&empId=ME&page=&pageSize=

import React, { useEffect, useState, useCallback, useMemo } from "react";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import { exportAllPagesToCsv, fetchAllPages } from "../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Card, CardBody, Input, Badge, Alert,
    Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader,
    EmptyState,
    TableSkeleton,
    ColumnFilterRow,
    applyColumnFilters,
    // R44/R46 — applyColumnFilters dropped (server filters now);
    //         useDebouncedValue dropped (text inputs now commit on Enter/blur).
} from "../../Components/Common/DashboardKit";
import api from "../../api";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";
import useAuth from "../Personal Attendance/useAuth";

const StatusBadge = ({ status }) => {
    const s = String(status || "").toUpperCase();
    const color =
        s === "APPROVED" ? "success" :
        s === "PENDING"  ? "warning" :
        s === "DENIED" || s === "VOID" ? "danger" : "secondary";
    return <Badge color={color} pill style={{ fontSize: 11 }}>{s || "—"}</Badge>;
};

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};
const pick = (r, ...keys) => {
    for (const k of keys) {
        if (r != null && r[k] !== undefined && r[k] !== null) return r[k];
    }
    return undefined;
};

// Why-not-ready label, used as the value for the "Reason" column so that
// the column-filter dropdown can match against it.
const reasonText = (row) => {
    const status = String(pick(row, "Status", "status") || "").toUpperCase();
    const received = pick(row, "Received", "received");
    if (status === "PENDING") return "Awaiting approval";
    if (status === "DENIED")  return "Denied";
    if (!received)            return "Items not received";
    return "Missing attachment";
};

const ReasonBadge = ({ row }) => {
    const t = reasonText(row);
    const color =
        t === "Awaiting approval" ? "info" :
        t === "Denied"            ? "danger" :
        t === "Items not received" ? "warning" :
        "secondary";
    return <Badge color={color} pill style={{ fontSize: 11 }}>{t}</Badge>;
};

const EposNotReadyForPayment = () => {
    const navigate = useNavigate();
    const epoReview = useEpoReviewModal({ permissions: { canPay: true, canFinanceEdit: true, canReceive: true } });
    const auth = useAuth();
    const empId = auth.empId;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [columnFilters, setColumnFilters] = useState({});
    // R46 — columnFilters dropped; columnFilters is itself the
    //         committed value (ColumnFilterRow defers text updates until
    //         Enter or focus-out).

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Round 31 — "all rows" mode: first sort click loads the COMPLETE
    // dataset (same page-walking as Export all). Sorting, filters, and
    // paging then operate on ALL rows, instantly and client-side.
    const [allRows, setAllRows] = useState(null);
    const [loadingAll, setLoadingAll] = useState(false);
    const loadAllRows = async () => {
        if (loadingAll || allRows) return;
        setLoadingAll("Loading all…");
        try {
            const fetched = await fetchAllPages({
                onProgress: (n, t) => setLoadingAll(`Loading all ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum, type: "PENDINGRECEIPT", empId };
                    const { data } = await api.get("Epo/all", { params });
                    const list = Array.isArray(data?.results) ? data.results
                               : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
            setAllRows(fetched);
            setPage(1);
        } catch (e) { /* keep server mode on failure */ }
        setLoadingAll(false);
    };
    // Round 29 — full CSV export: same endpoint + filters as the table,
    // looped across all pages (the visible page is just a window).
    const [exporting, setExporting] = useState(false);
    const handleExportAll = async () => {
        try {
            setExporting("Preparing…");
            await exportAllPagesToCsv({
                filename: "epos-not-ready-all.csv",
                columns: columns.filter((c) => c.type !== "none"),
                total,
                onProgress: (n, t) => setExporting(`Exporting ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum, type: "PENDINGRECEIPT", empId };
                    for (const [k, v] of Object.entries(columnFilters || {})) {
                        if (v == null || v === "") continue;
                        params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
                    }
                    const { data } = await api.get("Epo/all", { params });
                    const list = Array.isArray(data?.results) ? data.results
                               : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
        } catch (e) {
            setExporting(false);
            return;
        }
        setExporting(false);
    };


    // R44 — server-side column filtering.
    const load = useCallback((pageNum, sizeNum, filters) => {
        if (!empId) { setLoading(false); return () => {}; }
        let alive = true;
        setLoading(true);
        setError(null);
        const params = {
            type: "PENDINGRECEIPT",
            empId,
            page: pageNum ?? 1,
            pageSize: sizeNum ?? 25,
        };
        for (const [k, v] of Object.entries(filters || {})) {
            if (v == null || v === "") continue;
            // R44 — "reason" is a synthetic FE-only column, skip server-side.
            if (k === "reason") continue;
            // R44 — send as camelCase filter<Col>; .NET binds case-insensitively.
            params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
        }
        api.get("Epo/all", { params })
            .then(({ data }) => {
                if (!alive) return;
                const list = Array.isArray(data?.results) ? data.results
                           : Array.isArray(data?.Results) ? data.Results
                           : [];
                setRows(list);
                const t  = data?.Total      ?? data?.total      ?? 0;
                const tp = data?.TotalPages ?? data?.totalPages ?? 1;
                const sp = data?.Page       ?? data?.page       ?? (pageNum ?? 1);
                setTotal(t);
                setTotalPages(Math.max(1, tp));
                setPage(sp);
            })
            .catch((e) => { if (alive) setError(e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [empId]);

    // R44 — initial + filter-change load.
    useEffect(() => { setPage(1); if (allRows) return; return load(1, pageSize, columnFilters); }, [load, columnFilters, allRows]);  // eslint-disable-line

    const goToPage = (n) => {
        const t = Math.max(1, Math.min(effTotalPages, n));
        if (t === page) return;
        if (allRows) { setPage(t); return; } // Round 31 — local paging
        load(t, pageSize, columnFilters);  // R44
    };
    const changePageSize = (sz) => {
        const s2 = Number(sz) || 25;
        setPageSize(s2);
        if (allRows) { setPage(1); return; }
        load(1, s2, columnFilters);  // R44
    };

    const columns = useMemo(() => [
        { id: "epoNumber",   label: "EPO #",         type: "text",
          get: r => pick(r, "ePONumber", "EPONumber", "epoNumber") },
        { id: "createdDate", label: "Created",       type: "text",
          get: r => fmtDate(pick(r, "createdDate", "CreatedDate")) },
        { id: "purpose",     label: "Purpose",       type: "text",
          get: r => pick(r, "epoPurpose", "EpoPurpose") },
        { id: "status",      label: "Status",        type: "select",
          options: [
            { value: "APPROVED", label: "Approved" },
            { value: "PENDING",  label: "Pending"  },
            { value: "DENIED",   label: "Denied"   },
          ],
          get: r => String(pick(r, "status", "Status") || "").toUpperCase() },
        { id: "reason",      label: "Reason",        type: "select",
          options: [
            { value: "Awaiting approval",  label: "Awaiting approval"  },
            { value: "Denied",             label: "Denied"             },
            { value: "Items not received", label: "Items not received" },
            { value: "Missing attachment", label: "Missing attachment" },
          ],
          get: reasonText },
        { id: "approxTotal", label: "Approx. Total", type: "text",
          get: r => {
            const v = pick(r, "approxTotal", "ApproxTotal");
            return v != null ? String(v) : "";
          }},
        { id: "actions",     label: "",              type: "none" },
    ], []);

    // R44 — server returns already-filtered rows. The "reason" column is
    //        derived (from status + received + attachment) so it has to filter
    //        client-side; other filters are server-side.
    const visibleRowsBase = useMemo(() => {
        if (!columnFilters.reason) return rows;
        // Find just the reason column and filter on it locally.
        const reasonCol = columns.find(c => c.id === "reason");
        if (!reasonCol) return rows;
        return rows.filter(r =>
            String(reasonCol.get(r) || "").toLowerCase() === String(columnFilters.reason).toLowerCase()
        );
    }, [rows, columns, columnFilters.reason]);

    // Round 30/31 — in all-mode every filter (incl. reason) applies client-
    // side over the COMPLETE dataset; sort covers it all.
    const sortInput = allRows
        ? applyColumnFilters(allRows, columns, columnFilters)
        : visibleRowsBase;
    const { sorted: visibleRows, sort, toggleSort } = useTableSort(sortInput, columns);
    const onSortToggle = (id) => {
        if (!allRows && !loadingAll) loadAllRows();
        toggleSort(id);
    };
    const effTotal = allRows ? visibleRows.length : total;
    const effTotalPages = allRows ? Math.max(1, Math.ceil(visibleRows.length / pageSize)) : totalPages;
    const pagedRows = allRows
        ? visibleRows.slice((page - 1) * pageSize, page * pageSize)
        : visibleRows;
    const w = 7;
    let lo = Math.max(1, page - 3);
    let hi = Math.min(effTotalPages, lo + w - 1);
    lo = Math.max(1, hi - w + 1);
    const nums = [];
    for (let i = lo; i <= hi; i++) nums.push(i);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="EPOs Not Ready For Payment"
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "EPOs Not Ready For Payment" }]}
                    subtitle={`My EPOs awaiting approval, receipt, or attachment (${total} total)`}
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Not Ready For Payment" }]}
                />

                {error && <Alert color="danger" className="mt-2" toggle={() => setError(null)}>{error}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        {loading ? (
                            <div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>{columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            <TableSkeleton rows={6} columns={columns.length} />
                                        </tbody>
                                    </table>
                                </div>
                        ) : rows.length === 0 ? (
                            <EmptyState
                                title="All caught up"
                                description="None of your EPOs are blocked."
                            />
                        ) : (
                            <>
                                                                {/* Round 29 — exports ALL rows matching the current filters (walks the same API, all pages) */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm" disabled={!!exporting || !total} onClick={handleExportAll}>
                                        <i className="ri-download-2-line align-bottom me-1" />
                                        {exporting ? exporting : `Export all CSV (${total})`}
                                    </button>
                                                                    {loadingAll ? (
                                        <span className="badge bg-primary-subtle text-primary align-self-center ms-2">{loadingAll}</span>
                                    ) : allRows ? (
                                        <span className="badge bg-success-subtle text-success align-self-center ms-2" title="Sorting and filters now cover the full dataset">All {allRows.length} rows loaded</span>
                                    ) : null}
                                </div>
<div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                {columns.map((c) => (
                                                    <SortableTh key={c.id} col={c} sort={sort} onToggle={onSortToggle}
                                                        className={c.id === "approxTotal" ? "text-end" : ""} />
                                                ))}
                                            </tr>
                                            <ColumnFilterRow
                                                columns={columns}
                                                filters={columnFilters}
                                                onChange={setColumnFilters}
                                            />
                                        </thead>
                                        <tbody>
                                            {pagedRows.map((r, idx) => {
                                                const epoNum    = pick(r, "ePONumber", "EPONumber", "epoNumber");
                                                const createdAt = pick(r, "createdDate", "CreatedDate");
                                                const purpose   = pick(r, "epoPurpose", "EpoPurpose");
                                                const status    = pick(r, "status", "Status");
                                                const totalAmt  = pick(r, "approxTotal", "ApproxTotal");
                                                // R45 — Edit only when row.isEditable is true.
                                                const canEdit   = !!pick(r, "isEditable", "IsEditable");
                                                return (
                                                    <tr key={epoNum ?? idx} onDoubleClick={() => epoReview.open(epoNum)} style={{ cursor: "pointer" }}>
                                                        <td><strong>{epoNum}</strong></td>
                                                        <td>{fmtDate(createdAt)}</td>
                                                        <td style={{ maxWidth: 320 }}>{purpose}</td>
                                                        <td><StatusBadge status={status} /></td>
                                                        <td><ReasonBadge row={r} /></td>
                                                        <td className="text-end">{fmtMoney(totalAmt)}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0 me-2"
                                                                onClick={() => epoReview.open(epoNum)}
                                                            >View</button>
                                                            {canEdit && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link btn-sm p-0"
                                                                    onClick={() => navigate(`/epo?mode=edit&epoNumber=${epoNum}`)}
                                                                >Edit</button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {visibleRows.length === 0 && (
                                    <div className="text-muted text-center py-3 small">
                                        No rows match the current filters.
                                    </div>
                                )}

                                {total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted small">Rows per page:</span>
                                            <Input type="select" bsSize="sm" value={pageSize}
                                                onChange={(e) => changePageSize(e.target.value)} style={{ width: 80 }}>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                            </Input>
                                            <span className="text-muted small">
                                                {(allRows || visibleRows.length === rows.length)
                                                    ? <>Showing {effTotal === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, effTotal)} of {effTotal}{allRows ? " (all loaded)" : ""}</>
                                                    : <>Showing {visibleRows.length} of {rows.length} on this page (of {total} total)</>
                                                }
                                            </span>
                                        </div>
                                        <Pagination size="sm" className="mb-0">
                                            <PaginationItem disabled={page <= 1}><PaginationLink first onClick={() => goToPage(1)} /></PaginationItem>
                                            <PaginationItem disabled={page <= 1}><PaginationLink previous onClick={() => goToPage(page - 1)} /></PaginationItem>
                                            {nums.map((n) => (
                                                <PaginationItem key={n} active={n === page}>
                                                    <PaginationLink onClick={() => goToPage(n)}>{n}</PaginationLink>
                                                </PaginationItem>
                                            ))}
                                            <PaginationItem disabled={page >= effTotalPages}><PaginationLink next onClick={() => goToPage(page + 1)} /></PaginationItem>
                                            <PaginationItem disabled={page >= effTotalPages}><PaginationLink last onClick={() => goToPage(effTotalPages)} /></PaginationItem>
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        {epoReview.element}
        </div>
    );
};

export default EposNotReadyForPayment;
